/**
 * ACC Product Recommendations
 * Fetches recommendations via Shopify's recommendations API on intersection,
 * then provides carousel prev/next navigation via scroll.
 */
import { initAccCarousel } from '@theme/acc-carousel';

class AccProductRecommendations extends HTMLElement {
  #intersectionObserver = null;
  #cleanupCarousel = null;
  #abortController = null;
  #resizeFrame = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const performed = this.dataset.recommendationsPerformed === 'true';

    if (!performed) {
      this.#intersectionObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0]?.isIntersecting) return;
          observer.disconnect();
          this.#loadRecommendations();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.#intersectionObserver.observe(this);
    }

    this.#syncCarousel();

    window.addEventListener('resize', this.#scheduleSyncCarousel, {
      passive: true,
      signal: this.#abortController.signal,
    });

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.#handleSectionLoad);
    }
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#destroyCarousel();
    this.#abortController?.abort();
    if (this.#resizeFrame !== null) {
      window.cancelAnimationFrame(this.#resizeFrame);
      this.#resizeFrame = null;
    }
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:load', this.#handleSectionLoad);
    }
  }

  #handleSectionLoad = (event) => {
    if (event.detail?.sectionId === this.dataset.sectionId) {
      this.#syncCarousel();
    }
  };

  #scheduleSyncCarousel = () => {
    if (this.#resizeFrame !== null) return;

    this.#resizeFrame = window.requestAnimationFrame(() => {
      this.#resizeFrame = null;
      this.#syncCarousel();
    });
  };

  #destroyCarousel() {
    this.#cleanupCarousel?.();
    this.#cleanupCarousel = null;
  }

  #getVisibleCapacity() {
    if (window.matchMedia?.('(max-width: 749px)').matches) {
      return Number.parseInt(this.dataset.mobileColumns, 10) || 1;
    }

    if (window.matchMedia?.('(max-width: 989px)').matches) {
      return Number.parseInt(this.dataset.tabletColumns, 10) || 2;
    }

    return Number.parseInt(this.dataset.desktopColumns, 10) || 3;
  }

  #getRenderedProductCount(track) {
    const renderedCount = Number.parseInt(this.dataset.renderedProductsCount, 10);

    if (Number.isFinite(renderedCount) && renderedCount > 0) {
      return renderedCount;
    }

    return track?.querySelectorAll('.acc-product-recommendations__slide').length ?? 0;
  }

  #syncCarousel() {
    const track = this.querySelector('[ref="track"]');
    const prevBtn = this.querySelector('[ref="prevBtn"]');
    const nextBtn = this.querySelector('[ref="nextBtn"]');
    const slideCount = this.#getRenderedProductCount(track);
    const visibleCapacity = this.#getVisibleCapacity();
    const activeColumns = Math.max(1, Math.min(slideCount || visibleCapacity, visibleCapacity));

    this.style.setProperty('--acc-rec-active-columns', String(activeColumns));

    if (!track || !prevBtn || !nextBtn || slideCount <= visibleCapacity) {
      this.dataset.carouselActive = 'false';
      this.#destroyCarousel();
      if (track) track.scrollTo({ left: 0, behavior: 'auto' });
      if (prevBtn) prevBtn.hidden = true;
      if (nextBtn) nextBtn.hidden = true;
      return;
    }

    this.dataset.carouselActive = 'true';

    if (this.#cleanupCarousel) return;

    this.#cleanupCarousel = initAccCarousel({
      root: this,
      track,
      prevBtn,
      nextBtn,
      slideSelector: '.acc-product-recommendations__slide',
    });
  }

  async #loadRecommendations() {
    const { productId, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) return;

    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        this.#handleError(`Server returned ${response.status}`);
        return;
      }

      const html = await response.text();
      if (!html.trim().length) {
        this.#handleError('No recommendations available');
        return;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector('acc-product-recommendations');

      if (newSection) {
        this.#injectSectionStylesheet(doc);
        Array.from(newSection.attributes).forEach(({ name, value }) => {
          if (name.startsWith('data-')) {
            this.setAttribute(name, value);
          }
        });
        this.innerHTML = newSection.innerHTML;
        this.dataset.recommendationsPerformed = 'true';
        this.#syncCarousel();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.#handleError(error.message);
      }
    }
  }

  #injectSectionStylesheet(doc) {
    const newStylesheet = doc.querySelector('style[data-section-stylesheet]');
    const sectionElement = this.closest('.shopify-section');

    if (!newStylesheet || !sectionElement) return;

    const existingStylesheet = sectionElement.querySelector('style[data-section-stylesheet]');

    if (existingStylesheet) {
      existingStylesheet.textContent = newStylesheet.textContent;
    } else {
      sectionElement.prepend(newStylesheet);
    }
  }

  #handleError(message) {
    console.error('ACC Product recommendations error:', message);
    this.hidden = true;
  }
}

if (!customElements.get('acc-product-recommendations')) {
  customElements.define('acc-product-recommendations', AccProductRecommendations);
}
